/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCreateSingleViewMutation } from '../../createView/hooks/useCreateSingleViewMutation';
import { useCreateSingleViewWithVisualizationMutation } from '../../createView/hooks/useCreateSingleViewWithVisualizationMutation';
import { useFetchIsViewNameUsed } from '../../viewName/hooks/useFetchIsViewNameUsed';
import { useFetchViewsCounts } from '../../viewsCounts/hooks/useFetchViewsCounts';
import { useDebouncedValue } from '../../cloneView/hooks/useDebouncedValue';
import { useCreateViewModalErrors } from './useCreateViewModalErrors';
import invariant from 'react-utils/invariant';
import { TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { VIEW_NAMESPACE_NONE } from '../../viewsSearch/constants/viewNamespaces';
import { useBETCanCurrentUserMakePublicView } from '../../manageSharing/useBETCanCurrentUserMakePublicView';
const getCreatedViewFromResponse = (response, useVisualization) => {
  const fieldName = useVisualization ? 'createSingleViewWithVisualization' : 'createSingleView';
  return response.data[fieldName];
};
export const useCreateViewModalState = ({
  viewObject,
  onSuccess,
  onFailure,
  createStrategy,
  visualizationSettingsMap,
  canOnlyCreatePrivateViews
}) => {
  const {
    data: hasBETPermission
  } = useBETCanCurrentUserMakePublicView();

  // User can create public views if they have BET permission AND are not restricted to private views only
  const hasPublicPermission = hasBETPermission && !canOnlyCreatePrivateViews;
  const [viewSharingUpdates, setViewSharingUpdates] = useState({
    private: viewObject.private,
    teamId: viewObject.teamId,
    permissionDetails: viewObject.objectTypeId === TASK_TYPE_ID ? viewObject.permissionDetails : undefined
  });

  // Update private state when permission loads
  useEffect(() => {
    if (hasPublicPermission !== undefined && !viewObject.private) {
      // Only update if view was originally public and permission is now loaded
      if (!hasPublicPermission) {
        setViewSharingUpdates(prev => Object.assign({}, prev, {
          private: true
        }));
      }
    }
  }, [hasPublicPermission, viewObject.private]);
  const {
    loading: isCountsLoading,
    data: countsData,
    error: isCountsError
  } = useFetchViewsCounts(viewObject.objectTypeId);
  const [textInputValue, setTextInputValue] = useState(createStrategy === 'CREATE_NEW' ? '' : `${viewObject.name} clone`);
  const debouncedName = useDebouncedValue(textInputValue, 300);
  const isDebouncePending = textInputValue !== debouncedName;
  const {
    loading: isNameUsedLoading,
    data: isNameUsedData,
    error: isNameUsedError,
    refetch: isNameUsedRefetch
  } = useFetchIsViewNameUsed({
    objectTypeId: viewObject.objectTypeId,
    viewName: debouncedName
  });
  const isNameUsed = Boolean(isNameUsedData && isNameUsedData.isNameUsed);
  const viewToCreate = useMemo(() => {
    invariant(viewObject.id === undefined, 'you are attempting to create a view with an id - this is not supported');
    invariant(viewObject.ownerId === undefined, 'you are attempting to create a view with an id - this is not supported');
    const merged = Object.assign({}, viewObject, {
      name: textInputValue
    }, viewSharingUpdates);
    if (merged.private === undefined) {
      merged.private = false;
    }
    return merged;
  }, [textInputValue, viewSharingUpdates, viewObject]);
  // Determine if we should use the new visualization endpoint
  const shouldUseVisualizationEndpoint = createStrategy === 'CLONE_EXISTING' && Boolean(visualizationSettingsMap) && viewObject.namespaceName === VIEW_NAMESPACE_NONE;

  // Create view object with visualization settings for the mutation
  const viewForVisualizationMutation = useMemo(() => {
    if (shouldUseVisualizationEndpoint) {
      return Object.assign({}, viewToCreate, {
        visualizationSettingsMap
      });
    }
    return viewToCreate;
  }, [viewToCreate, visualizationSettingsMap, shouldUseVisualizationEndpoint]);

  // Use the appropriate mutation based on the context
  const [createViewStandard, {
    loading: createSingleViewMutationLoadingStandard,
    error: createSingleViewMutationErrorStandard
  }] = useCreateSingleViewMutation(viewToCreate);
  const [createViewWithVisualization, {
    loading: createSingleViewMutationLoadingViz,
    error: createSingleViewMutationErrorViz
  }] = useCreateSingleViewWithVisualizationMutation(viewForVisualizationMutation);

  // Determine which mutation to use
  const createView = shouldUseVisualizationEndpoint ? createViewWithVisualization : createViewStandard;
  const createSingleViewMutationLoading = shouldUseVisualizationEndpoint ? createSingleViewMutationLoadingViz : createSingleViewMutationLoadingStandard;
  const createSingleViewMutationError = shouldUseVisualizationEndpoint ? createSingleViewMutationErrorViz : createSingleViewMutationErrorStandard;
  const isLoading = Boolean(isCountsLoading || isDebouncePending || createSingleViewMutationLoading || isNameUsedLoading);
  const isFetchError = Boolean(isCountsError || isNameUsedError);
  const [isError, errorAffordance, errorValidationMessage] = useCreateViewModalErrors({
    isNameEmpty: debouncedName === '',
    isNameUsed,
    viewCountLimit: countsData && countsData.limit,
    viewCountValue: countsData && countsData.currentValue,
    isFetchError,
    isCreateError: Boolean(createSingleViewMutationError),
    createStrategy
  });
  const handleSubmit = useCallback(event => {
    event.preventDefault();
    createView().then(response => {
      if (response && response.data) {
        const createdView = getCreatedViewFromResponse(response, shouldUseVisualizationEndpoint);
        onSuccess({
          createdView
        });
      }
    }).catch(e => {
      onFailure(e);
    });
  }, [createView, onSuccess, onFailure, shouldUseVisualizationEndpoint]);
  const textInputOnChange = useCallback(e => {
    setTextInputValue(e.target.value);
  }, []);

  /*
   * every time debouncedName changes, make an api call to see if its used
   */
  useEffect(() => {
    if (debouncedName) {
      isNameUsedRefetch({
        viewName: debouncedName,
        objectTypeId: viewObject.objectTypeId
      });
    }
  }, [debouncedName, isNameUsedRefetch, viewObject.objectTypeId]);
  return {
    isLoading,
    isError,
    errorValidationMessage,
    errorAffordance,
    handleSubmit,
    textInputOnChange,
    textInputValue,
    viewSharingUpdates,
    setViewSharingUpdates
  };
};